import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { PageHeader, Card, Modal } from '../components/ui';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';

const tom = () => ({
  medlemsnr: '', fornavn: '', etternavn: '', fodselsdato: '', kjonn: '',
  epost: '', telefon: '', adresse: '', postnr: '', sted: '', land: 'Norge',
  foresatt_navn: '', foresatt_telefon: '', foresatt_epost: '',
  status: 'aktiv', innmeldt_dato: '', utmeldt_dato: '',
  samtykke_lagring: true, samtykke_kommunikasjon: false, skattefradrag_aktiv: false,
  notat: '',
});

const STATUSER = [
  { v: 'aktiv', t: 'Aktiv', cls: 'badge-success' },
  { v: 'passiv', t: 'Passiv', cls: 'badge-warning' },
  { v: 'utmeldt', t: 'Utmeldt', cls: 'badge-info' },
  { v: 'avdod', t: 'Avdød', cls: 'badge-danger' },
];

const Medlemmer = () => {
  const [status, setStatus] = useState('alle');
  const [sok, setSok] = useState('');
  const [liste, setListe] = useState(null);
  const [open, setOpen] = useState(false);
  const [redigerer, setRedigerer] = useState(null);
  const [skjema, setSkjema] = useState(tom());

  const last = useCallback(() => {
    const params = {};
    if (status !== 'alle') params.status = status;
    api.get('/medlemmer', { params }).then((r) => setListe(r.data));
  }, [status]);
  useEffect(() => { last(); }, [last]);

  const aapne = (m) => {
    setRedigerer(m);
    setSkjema(m ? { ...tom(), ...m } : tom());
    setOpen(true);
  };
  const lagre = async () => {
    if (!skjema.fornavn.trim()) { alert('Fornavn er påkrevd.'); return; }
    if (redigerer) await api.put(`/medlemmer/${redigerer.id}`, skjema);
    else await api.post('/medlemmer', skjema);
    setOpen(false); last();
  };
  const slett = async (m) => {
    if (!window.confirm(`Slett medlem ${m.navn}?`)) return;
    await api.delete(`/medlemmer/${m.id}`); last();
  };

  if (!liste) return <span className="fm-text-muted">Laster…</span>;

  const filtrert = liste.filter((m) => {
    if (!sok.trim()) return true;
    const q = sok.toLowerCase();
    return [m.navn, m.medlemsnr, m.epost, m.telefon, m.sted, m.adresse]
      .some((v) => (v || '').toString().toLowerCase().includes(q));
  });

  const stat = (s) => STATUSER.find(x => x.v === s) || STATUSER[0];

  return (
    <>
      <PageHeader
        title="Medlemmer"
        actions={
          <button className="btn-primary" onClick={() => aapne(null)}>
            <Plus size={14} /> Nytt medlem
          </button>
        }
      />
      <Card style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 220 }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%',
                                       transform: 'translateY(-50%)', opacity: 0.55 }} />
            <input value={sok} onChange={(e) => setSok(e.target.value)}
                   placeholder="Søk navn, medlemsnr, e-post, telefon…"
                   style={{ paddingLeft: 32, width: '100%' }} />
          </div>
          <label style={{ margin: 0 }}>Status:</label>
          <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ width: 180 }}>
            <option value="alle">Alle</option>
            {STATUSER.map(s => <option key={s.v} value={s.v}>{s.t}</option>)}
          </select>
          <span className="fm-text-muted" style={{ fontSize: 13 }}>{filtrert.length} medlemmer</span>
        </div>
      </Card>
      <Card style={{ padding: 0 }}>
        <table>
          <thead>
            <tr>
              <th>Medlemsnr</th>
              <th>Navn</th>
              <th>Status</th>
              <th>E-post</th>
              <th>Telefon</th>
              <th>Sted</th>
              <th>Grupper</th>
              <th style={{ width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {filtrert.length === 0 && (
              <tr><td colSpan={8} className="fm-text-muted" style={{ textAlign: 'center', padding: 20 }}>Ingen medlemmer.</td></tr>
            )}
            {filtrert.map((m) => {
              const s = stat(m.status);
              return (
                <tr key={m.id}>
                  <td>{m.medlemsnr}</td>
                  <td><strong>{m.navn}</strong></td>
                  <td><span className={`badge ${s.cls}`}>{s.t}</span></td>
                  <td>{m.epost}</td>
                  <td>{m.telefon}</td>
                  <td>{[m.postnr, m.sted].filter(Boolean).join(' ')}</td>
                  <td style={{ fontSize: 12 }}>
                    {(m.grupper || []).map(g => (
                      <span key={g.id} className="badge badge-info" style={{ marginRight: 4 }}>
                        {g.gruppe_navn}{g.verv && g.verv !== 'medlem' ? ` (${g.verv})` : ''}
                      </span>
                    ))}
                  </td>
                  <td>
                    <button className="btn-ghost" onClick={() => aapne(m)} title="Rediger"><Pencil size={12} /></button>
                    <button className="btn-ghost" onClick={() => slett(m)} title="Slett" style={{ color: 'var(--theme-danger)' }}><Trash2 size={12} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Modal open={open} title={redigerer ? 'Rediger medlem' : 'Nytt medlem'}
             onClose={() => setOpen(false)} width={720}
             footer={<>
               <button className="btn-ghost" onClick={() => setOpen(false)}>Avbryt</button>
               <button className="btn-primary" onClick={lagre}>Lagre</button>
             </>}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <label>Medlemsnr<input value={skjema.medlemsnr}
            onChange={(e) => setSkjema({ ...skjema, medlemsnr: e.target.value })} /></label>
          <label>Status
            <select value={skjema.status}
              onChange={(e) => setSkjema({ ...skjema, status: e.target.value })}>
              {STATUSER.map(s => <option key={s.v} value={s.v}>{s.t}</option>)}
            </select>
          </label>
          <label>Fornavn *<input value={skjema.fornavn}
            onChange={(e) => setSkjema({ ...skjema, fornavn: e.target.value })} required /></label>
          <label>Etternavn<input value={skjema.etternavn}
            onChange={(e) => setSkjema({ ...skjema, etternavn: e.target.value })} /></label>
          <label>Fødselsdato<input type="date" value={skjema.fodselsdato}
            onChange={(e) => setSkjema({ ...skjema, fodselsdato: e.target.value })} /></label>
          <label>Kjønn
            <select value={skjema.kjonn}
              onChange={(e) => setSkjema({ ...skjema, kjonn: e.target.value })}>
              <option value="">—</option>
              <option value="K">Kvinne</option>
              <option value="M">Mann</option>
              <option value="annet">Annet</option>
            </select>
          </label>
          <label>E-post<input type="email" value={skjema.epost}
            onChange={(e) => setSkjema({ ...skjema, epost: e.target.value })} /></label>
          <label>Telefon<input value={skjema.telefon}
            onChange={(e) => setSkjema({ ...skjema, telefon: e.target.value })} /></label>
          <label style={{ gridColumn: '1 / -1' }}>Adresse<input value={skjema.adresse}
            onChange={(e) => setSkjema({ ...skjema, adresse: e.target.value })} /></label>
          <label>Postnr<input value={skjema.postnr}
            onChange={(e) => setSkjema({ ...skjema, postnr: e.target.value })} /></label>
          <label>Sted<input value={skjema.sted}
            onChange={(e) => setSkjema({ ...skjema, sted: e.target.value })} /></label>
          <label>Innmeldt<input type="date" value={skjema.innmeldt_dato}
            onChange={(e) => setSkjema({ ...skjema, innmeldt_dato: e.target.value })} /></label>
          <label>Utmeldt<input type="date" value={skjema.utmeldt_dato}
            onChange={(e) => setSkjema({ ...skjema, utmeldt_dato: e.target.value })} /></label>

          <fieldset style={{ gridColumn: '1 / -1', border: '1px solid var(--theme-border)',
                             borderRadius: 6, padding: 10 }}>
            <legend style={{ padding: '0 6px', fontSize: 12 }}>Foresatt (under 18)</legend>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
              <label>Navn<input value={skjema.foresatt_navn}
                onChange={(e) => setSkjema({ ...skjema, foresatt_navn: e.target.value })} /></label>
              <label>Telefon<input value={skjema.foresatt_telefon}
                onChange={(e) => setSkjema({ ...skjema, foresatt_telefon: e.target.value })} /></label>
              <label>E-post<input value={skjema.foresatt_epost}
                onChange={(e) => setSkjema({ ...skjema, foresatt_epost: e.target.value })} /></label>
            </div>
          </fieldset>

          <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={skjema.samtykke_lagring}
                onChange={(e) => setSkjema({ ...skjema, samtykke_lagring: e.target.checked })} />
              Samtykke til lagring (GDPR)
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={skjema.samtykke_kommunikasjon}
                onChange={(e) => setSkjema({ ...skjema, samtykke_kommunikasjon: e.target.checked })} />
              Samtykke til e-post/SMS
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={skjema.skattefradrag_aktiv}
                onChange={(e) => setSkjema({ ...skjema, skattefradrag_aktiv: e.target.checked })} />
              Skattefradrag aktivt
            </label>
          </div>

          <label style={{ gridColumn: '1 / -1' }}>Notat
            <textarea rows={3} value={skjema.notat}
              onChange={(e) => setSkjema({ ...skjema, notat: e.target.value })} />
          </label>
        </div>
      </Modal>
    </>
  );
};

export default Medlemmer;
